import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import BrandLogosStrip from '@/pages/home/components/BrandLogosStrip';

function useVisible(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

const services = [
  {
    num: '01',
    icon: 'ri-building-2-line',
    title: 'Hotel Management',
    category: 'Operations',
    description:
      'Best-in-class hotel operations tailored to each asset\'s unique needs. We deliver measurable improvements in guest satisfaction, operational efficiency, and GOP margins across branded, independent, select service, full service, and extended stay properties. Our teams are built on integrity, agility, and proven performance.',
    tags: ['Branded', 'Independent', 'Full Service', 'Select Service'],
  },
  {
    num: '02',
    icon: 'ri-megaphone-line',
    title: 'Sales & Marketing',
    category: 'Growth',
    description:
      'Strategy-driven sales and data-backed decisions. We deploy hyper-local outreach, regional penetration, and national account optimization — executed by a seasoned sales force that builds lasting client relationships. From corporate negotiated business and group segments to grassroots sales activations, we tailor every campaign to your property\'s competitive set and demand drivers.',
    tags: ['Corporate Accounts', 'Group Segments', 'Local Outreach'],
  },
  {
    num: '03',
    icon: 'ri-line-chart-line',
    title: 'Revenue Management',
    category: 'Revenue',
    description:
      'We drive ADR and premium revenue through tightly integrated revenue, sales, and digital marketing strategies. Leveraging advanced forecasting models, comp set analytics, and channel-agnostic revenue plans — we prioritize profitability across GDS, OTA, direct, and metasearch. Our goal: outperform your market and grow RevPAR index.',
    tags: ['OTA Strategy', 'GDS', 'Forecasting', 'RevPAR Growth'],
  },
  {
    num: '04',
    icon: 'ri-hammer-line',
    title: 'Development',
    category: 'Pre-Opening',
    description:
      'From blueprint to grand opening. Our pre-opening expertise covers underwriting, brand selection, hiring, training, systems integration, and go-to-market positioning. We have successfully launched properties across major brands and independent identities, always with long-term asset value in mind.',
    tags: ['Brand Selection', 'Pre-Opening', 'Systems Integration'],
  },
  {
    num: '05',
    icon: 'ri-tools-line',
    title: 'Renovation',
    category: 'CapEx',
    description:
      'We lead CapEx planning, brand negotiation, PIP evaluations, and full renovation oversight — ensuring capital is deployed efficiently to drive ROI. Transform your property with modern design, functional improvements, and brand-compliant upgrades that enhance guest satisfaction and property value.',
    tags: ['PIP Evaluation', 'CapEx Planning', 'Brand Compliance'],
  },
  {
    num: '06',
    icon: 'ri-shield-check-line',
    title: 'Quality Assurance',
    category: 'Standards',
    description:
      'Rigorous quality control through daily reporting, KPI tracking, inline audits, and competitive benchmarking. We maintain the highest standards across all properties through continuous improvement initiatives that protect your brand reputation and guest loyalty scores.',
    tags: ['KPI Tracking', 'Audits', 'Brand Standards'],
  },
  {
    num: '07',
    icon: 'ri-funds-box-line',
    title: 'Asset & Investment Management',
    category: 'Investment',
    description:
      'Your investment. Our priority. Across every stage of the lifecycle. We provide investment underwriting, feasibility studies, pro forma modeling, and comprehensive performance monitoring. From acquisitions and repositionings to exit strategies — we serve as your strategic fiduciary partner, always focused on risk mitigation and long-term value creation.',
    tags: ['Underwriting', 'Feasibility', 'Exit Strategy'],
  },
  {
    num: '08',
    icon: 'ri-calculator-line',
    title: 'Accounting & Finance',
    category: 'Finance',
    description:
      'Upholding financial integrity with precise and responsible fiscal management. We manage accounts receivable and payable, vendor agreements, cash reconciliations, and full financial reporting — giving owners complete transparency into property performance at every level.',
    tags: ['AR/AP', 'Financial Reporting', 'Cash Reconciliation'],
  },
  {
    num: '09',
    icon: 'ri-team-line',
    title: 'Human Resources',
    category: 'People',
    description:
      'Supporting a dynamic team environment where talent thrives. From hiring and training to compliance and culture-building, our HR services ensure your property attracts, develops, and retains top-performing hospitality professionals.',
    tags: ['Hiring', 'Training', 'Compliance', 'Culture'],
  },
  {
    num: '10',
    icon: 'ri-computer-line',
    title: 'Information Technology',
    category: 'Technology',
    description:
      'Leveraging advanced technology to enhance operational efficiency and the guest experience. From property management systems and channel managers to cybersecurity and digital infrastructure — we ensure your technology stack works hard for you.',
    tags: ['PMS', 'Channel Manager', 'Cybersecurity'],
  },
];

const categoryColors: Record<string, string> = {
  Operations: 'text-amber-400 bg-amber-500/10',
  Growth: 'text-emerald-400 bg-emerald-500/10',
  Revenue: 'text-sky-400 bg-sky-500/10',
  'Pre-Opening': 'text-violet-400 bg-violet-500/10',
  CapEx: 'text-orange-400 bg-orange-500/10',
  Standards: 'text-rose-400 bg-rose-500/10',
  Investment: 'text-amber-400 bg-amber-500/10',
  Finance: 'text-teal-400 bg-teal-500/10',
  People: 'text-pink-400 bg-pink-500/10',
  Technology: 'text-indigo-400 bg-indigo-500/10',
};

function ServicesHero() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative h-[520px] md:h-[620px] flex items-end overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Professional%20hotel%20management%20team%20meeting%20in%20bright%20modern%20conference%20room%20with%20floor%20to%20ceiling%20windows%20overlooking%20Florida%20palm%20trees%2C%20diverse%20business%20professionals%20discussing%20strategy%20around%20elegant%20table%20with%20laptops%20and%20documents%2C%20warm%20natural%20daylight%2C%20contemporary%20corporate%20hospitality%20setting%2C%20amber%20and%20slate%20accent%20colors%2C%20clean%20professional%20atmosphere%2C%20architectural%20photography%2C%20wide%20angle%20view&width=1920&height=900&seq=services-hero-360hg-v3&orientation=landscape"
          alt="360 Hospitality Group Services"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/65 to-slate-950/25"></div>
      </div>
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 pb-16 md:pb-20">
        <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-2 mb-5">
            <div className="h-px w-8 bg-amber-400"></div>
            <span className="text-xs font-medium text-amber-400 uppercase tracking-[0.25em]">What We Do</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-light text-white leading-tight tracking-tight mb-4">
            Ten Ways We<br />
            <span className="text-amber-400 italic">Move the Needle</span>
          </h1>
          <p className="text-lg text-gray-300 font-light max-w-2xl leading-relaxed">
            From daily operations to long-term asset strategy — we cover the full picture across every discipline that drives hotel performance.
          </p>
        </div>
      </div>
    </section>
  );
}

function ServicesTrustBar() {
  const items = [
    { icon: 'ri-building-4-line', label: 'IHG · Marriott' },
    { icon: 'ri-map-pin-2-line', label: 'All Florida Markets' },
    { icon: 'ri-time-line', label: '100+ Years Combined Experience' },
    { icon: 'ri-bar-chart-grouped-line', label: '600+ Rooms Under Management' },
    { icon: 'ri-trophy-line', label: 'Lowest Operational Cost In Industry' },
  ];
  return (
    <div className="bg-slate-900 border-b border-slate-800">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-5">
        <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
          {items.map((item, i) => (
            <div key={i} className="flex items-center space-x-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${item.icon} text-amber-500 text-base`}></i>
              </div>
              <span className="text-xs font-light text-gray-400 tracking-wide whitespace-nowrap">{item.label}</span>
              {i < items.length - 1 && <span className="hidden lg:block text-slate-700 ml-8">│</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ServicesGrid() {
  const { ref, visible } = useVisible();
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <section ref={ref} className="py-24 bg-slate-950">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className={`mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-2 mb-6">
            <div className="h-px w-8 bg-amber-500"></div>
            <span className="text-xs font-medium text-amber-500 uppercase tracking-[0.25em]">Full Service Suite</span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="text-4xl md:text-5xl font-light text-white leading-tight">
              Every Discipline.<br />
              <span className="text-amber-400 italic">One Partner.</span>
            </h2>
            <p className="text-base text-gray-400 font-light max-w-sm leading-relaxed">
              360 brings institutional-grade expertise across all ten service areas — so you never have to manage multiple vendors or fill the gaps yourself.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-px bg-slate-800/40">
          {services.map((service, index) => (
            <div
              key={index}
              onMouseEnter={() => setHovered(index)}
              onMouseLeave={() => setHovered(null)}
              className={`relative bg-slate-950 p-8 md:p-10 transition-all duration-500 cursor-default ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              } ${hovered === index ? 'bg-slate-900/80' : ''}`}
              style={{ transitionDelay: `${Math.min(index * 60, 400)}ms` }}
            >
              <div className="flex items-start gap-6">
                {/* Left: number + icon */}
                <div className="shrink-0 flex flex-col items-center gap-3 pt-1">
                  <span className="text-xs font-medium text-amber-500/50 tracking-[0.3em]">{service.num}</span>
                  <div className={`w-12 h-12 flex items-center justify-center rounded-xl transition-all duration-300 ${
                    hovered === index ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-800 text-gray-500'
                  }`}>
                    <i className={`${service.icon} text-2xl`}></i>
                  </div>
                </div>

                {/* Right: content */}
                <div className="flex-1 min-w-0">
                  {/* Category badge */}
                  <span className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full mb-3 ${categoryColors[service.category] ?? 'text-gray-400 bg-gray-800'}`}>
                    {service.category}
                  </span>

                  <h3 className={`text-xl font-medium mb-3 transition-colors duration-300 ${hovered === index ? 'text-amber-400' : 'text-white'}`}>
                    {service.title}
                  </h3>

                  <p className="text-sm text-gray-400 font-light leading-relaxed mb-5">
                    {service.description}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-5">
                    {service.tags.map((tag, ti) => (
                      <span key={ti} className="text-xs text-gray-500 border border-slate-700 rounded-full px-3 py-1 font-light">
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Learn More */}
                  <Link
                    to="/contact"
                    className={`inline-flex items-center space-x-1.5 text-xs font-medium uppercase tracking-widest transition-colors duration-300 ${
                      hovered === index ? 'text-amber-400' : 'text-gray-600'
                    }`}
                  >
                    <span>Learn More</span>
                    <i className="ri-arrow-right-line text-sm"></i>
                  </Link>
                </div>
              </div>

              {/* Bottom accent line */}
              <div className={`absolute bottom-0 left-0 h-0.5 bg-amber-500 transition-all duration-500 ${hovered === index ? 'w-full' : 'w-0'}`}></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServicesApproach() {
  const { ref, visible } = useVisible();
  const pillars = [
    { icon: 'ri-focus-3-line', title: 'Performance-First', body: 'Every service we deliver is measured against your KPIs. Our fee is tied to your results — not the other way around.' },
    { icon: 'ri-user-star-line', title: 'Senior-Led Execution', body: 'No junior reps. Every 360 client has a named senior partner with direct accountability for your property.' },
    { icon: 'ri-map-pin-2-line', title: 'Florida-Native Expertise', body: 'Born and built in Florida. We know every market, every demand driver, and every competitive dynamic — locally.' },
  ];
  return (
    <section ref={ref} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-2 mb-6">
            <div className="h-px w-8 bg-amber-500"></div>
            <span className="text-xs font-medium text-amber-600 uppercase tracking-[0.25em]">How We Deliver</span>
            <div className="h-px w-8 bg-amber-500"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-light text-slate-900 leading-tight">
            The 360 Difference
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {pillars.map((p, i) => (
            <div
              key={i}
              className={`text-center p-8 rounded-2xl bg-slate-50 border border-slate-100 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              <div className="w-14 h-14 flex items-center justify-center bg-amber-100 rounded-xl mx-auto mb-5">
                <i className={`${p.icon} text-2xl text-amber-600`}></i>
              </div>
              <h3 className="text-lg font-medium text-slate-900 mb-3">{p.title}</h3>
              <p className="text-sm text-slate-500 font-light leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServicesCTA() {
  const { ref, visible } = useVisible();
  return (
    <section ref={ref} className="py-24 bg-slate-950">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-2 mb-6">
            <div className="h-px w-8 bg-amber-500"></div>
            <span className="text-xs font-medium text-amber-500 uppercase tracking-[0.25em]">Ready to Partner?</span>
            <div className="h-px w-8 bg-amber-500"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-light text-white leading-tight mb-6">
            One Partner.<br />
            <span className="text-amber-400 italic">Every Discipline.</span>
          </h2>
          <p className="text-base text-gray-400 font-light leading-relaxed mb-10 max-w-xl mx-auto">
            No pitch deck. No junior rep. A direct conversation with a 360 senior partner about what your hotel should be doing better — this week.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/"
              className="group px-6 py-3 rounded-full bg-[#1a2b3c] hover:bg-[#243447] border border-amber-400/40 hover:border-amber-400/70 text-white text-sm font-medium tracking-wide transition-all duration-300 whitespace-nowrap flex items-center space-x-2"
            >
              <span>Get a Free Assessment</span>
              <i className="ri-arrow-right-line text-base transition-transform group-hover:translate-x-1"></i>
            </Link>
            <Link
              to="/performance"
              className="group px-6 py-3 rounded-full border border-white/30 text-white text-sm font-light tracking-wide hover:border-amber-400/60 hover:bg-white/5 transition-all duration-300 whitespace-nowrap flex items-center space-x-2"
            >
              <span>Our Performance Model</span>
              <i className="ri-arrow-right-line text-base text-amber-400 transition-transform group-hover:translate-x-1"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ServicesPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <ServicesHero />
      <BrandLogosStrip />
      <ServicesGrid />
      <ServicesApproach />
      <ServicesCTA />
      <Footer />
    </div>
  );
}
